import { Check, Minus, TriangleAlert, X } from "lucide-react";

import Eyebrow from "../../Eyebrow";
import Reveal from "../../Reveal";
import TechNote from "../TechNote";
import { howItWorksSections } from "../../../../content/howItWorksSections";

const statusIcons = { yes: Check, no: X, partial: TriangleAlert, na: Minus };

/**
 * Icon + short label pairing for a single comparison-table cell, with the
 * fuller explanation displayed underneath in italic.
 * Translation: Icon + short label pairing for a single comparison-table cell, with the
 * fuller explanation displayed underneath in italic.
 *
 * @param {Object} props - Status mark props.
 * @param {"yes"|"no"|"partial"|"na"} props.status - Which mark to render.
 * @param {string} props.short - Short label shown next to the mark.
 * @param {string} props.detail - Fuller explanation shown in italic below the label.
 * @returns {JSX.Element} Status mark.
 */
const StatusMark = ({ status, short, detail }) => {
  const Icon = statusIcons[status];

  return (
    <span className={`hiw-compare-mark hiw-compare-mark--${status}`}>
      <Icon className="hiw-compare-mark__icon" strokeWidth={2} aria-hidden="true" />
      <span className="hiw-compare-mark__body">
        <span className="hiw-compare-mark__short">{short}</span>
        <em className="hiw-compare-mark__detail">{detail}</em>
      </span>
    </span>
  );
};

/**
 * Condensed comparison table contrasting AgentOS against generic,
 * single-purpose AI agent tools, with a sourced disclaimer underneath.
 * Translation: Condensed comparison table contrasting AgentOS against generic,
 * single-purpose AI agent tools, with a sourced disclaimer underneath.
 *
 * @returns {JSX.Element} Comparison section.
 */
const Comparison = () => {
  const { comparison, techNotes } = howItWorksSections;

  return (
    <section id="comparison" className="section-block">
      <div className="section-shell">
        <Reveal>
          <Eyebrow>
            {comparison.eyebrowLabel}
            <TechNote>{techNotes[comparison.techNote]}</TechNote>
          </Eyebrow>
          <h2 className="section-heading__title">{comparison.title}</h2>
          <p className="section-heading__intro">{comparison.description}</p>
        </Reveal>

        <Reveal className="hiw-compare-wrap">
          <table className="hiw-compare-table">
            <thead>
              <tr>
                <th scope="col">Capability</th>
                <th scope="col" className="hiw-compare-us">Kanzi</th>
                <th scope="col">Generic AI Agents</th>
              </tr>
            </thead>
            <tbody>
              {comparison.rows.map((row) => (
                <tr key={row.capability}>
                  <th scope="row">{row.capability}</th>
                  <td className="hiw-compare-us">
                    <StatusMark status="yes" short={row.us.short} detail={row.us.detail} />
                  </td>
                  <td>
                    <StatusMark status={row.them.status} short={row.them.short} detail={row.them.detail} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <Reveal>
          <p className="hiw-compare-disclaimer">{comparison.disclaimer}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default Comparison;
